import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Image } from '@bingads-webui-react/fabric-control';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { ConditionalTooltipHost } from '@bingads-webui-campaign-react/conditional-tooltip-host';

import { getImageExtensionItemStyles } from '../styles/image-extensions-list-style';
import { getImageExtensionItemThumbnailUrl } from './utils/parser';

export function ImageExtensionItemPreview({
  i18n,
  onDelete,
  onSelect,
  onEditImage,
  enableEdit,
  index,
  item,
}) {
  const classes = useDefaultStyles(getImageExtensionItemStyles);
  const [hovered, setHovered] = useState(false);
  const toggleHoverState = () => { setHovered(!hovered); };
  const imageUrl = getImageExtensionItemThumbnailUrl(item);

  const disabledToggleToolTip = <div>{i18n.getString(_TL_('To use your image recommendations, you must enable '))}<b>{i18n.getString(_TL_('Automatically pull images from my Final URL.'))}</b></div>;

  return (
    <ConditionalTooltipHost
      condition={!!item.disabled}
      content={disabledToggleToolTip}
      calloutProps={{ gapSpace: 8 }}
    >
      <div
        className={classnames(
          classes.itemContainer,
          'image-extension-container',
          item.selected && 'selected',
          item.disabled && 'disabled',
          !item.disabled && hovered && 'hovered'
        )}
        onMouseEnter={hovered ? undefined : toggleHoverState}
        onMouseLeave={hovered ? toggleHoverState : undefined}
      >
        <div
          className={classnames(classes.imageExtensionItem, 'image-extension-item')}
          onClick={() => onSelect(index)}
          key={imageUrl}
          role="option"
          aria-selected={item.selected}
          id={`listbox-image-extension${index + 1}`}
          tabIndex={index === 0 ? 0 : -1}
          onKeyDown={submitEvent(() => onSelect(index))}
          data-is-focusable
        >
          { imageUrl ?
            <Image
              className={classes.imageContainer}
              src={imageUrl}
              alt={item.AltText}
            /> :
            <div className={classes.noImageFallbackContainer}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048">
                <path d="M1408 1600v-320h128v320h-128zm0 192v-128h128v128h-128zM0 128h2048v1434l-128-256V256H128v677l448-447 572 572-60 120-512-512-448 449v421h781l-64 128H0V128zm1600 384q-26 0-45-19t-19-45q0-26 19-45t45-19q26 0 45 19t19 45q0 26-19 45t-45 19zM896 1920l576-1152 576 1152H896zm181-112h790l-395-790-395 790z" />
              </svg>
            </div>
          }
        </div>
        <input
          aria-label="select-extension"
          type="radio"
          checked={item.selected}
          onChange={() => onSelect(index)}
          className={classnames(classes.selectRadio, 'image-extension-select')}
        />
        <Glyphicon
          onClick={(e) => {
          e.stopPropagation();
          onDelete(item);
        }}
          className={classnames(classes.deleteButton, 'image-extension-delete circular-hover')}
          glyph="ba-Cancel"
          iconStyle="primary"
          data-is-focusable
        />
        {enableEdit && <Glyphicon
          onClick={(e) => {
          e.stopPropagation();
          onEditImage(item, index);
        }}
          className={classnames(classes.editButton, 'image-extension-edit circular-hover')}
          glyph="ba-Edit"
          iconStyle="primary"
          data-is-focusable
        />}
      </div>
    </ConditionalTooltipHost>
  );
}

ImageExtensionItemPreview.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onEditImage: PropTypes.func.isRequired,
  enableEdit: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    AltText: PropTypes.string,
    selected: PropTypes.bool,
    disabled: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

ImageExtensionItemPreview.defaultProps = {
};

